import React, { useEffect, useState } from 'react'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid, IconButton, Skeleton, Tooltip, Typography } from '@mui/material'
import { useNotify, required, Form } from 'react-admin'
import { useTranslate } from 'react-admin'
import { installmentServices } from 'src/services/payment/installment/installment.service'
import { Add, Close, Delete, InsertDriveFile, Upload } from '@mui/icons-material'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'
import { sourcePaymentServices } from 'src/services/payment/source/source.service'
import { workUnitPaymentServices } from 'src/services/payment/workUnit/WorkUnit.service'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { blue } from 'src/assets/colors'
import { LoadingButton } from '@mui/lab'

export const InstallmentImportButton = () => {
  const notify = useNotify()
  const translate = useTranslate()
  const [loading, setLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [productTypeList, setProductTypeList] = useState([])
  const [loadingProductTypeList, setLoadingProductTypeList] = useState(false)
  const [workUnitList, setWorkUnitList] = useState([])
  const [loadingWorkUnitList, setLoadingWorkUnitList] = useState(false)
  const [sources, setSources] = useState([])
  const [loadingSources, setLoadingSources] = useState(false)
  const [file, setFile] = useState()
  const [fileError, setFileError] = useState(false)

  useEffect(() => {
    getProductTypes()
    getSources()
    getWorkUnit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getProductTypes = async () => {
    try {
      setLoadingProductTypeList(true)
      const allProducts = await productTypeServices.getByParam()
      setProductTypeList(allProducts)
      setLoadingProductTypeList(false)
    } catch (error) {
      setProductTypeList([])
      setLoadingProductTypeList(false)
    }
  }

  const getSources = async () => {
    try {
      setLoadingSources(true)
      const response = await sourcePaymentServices.getByAll()
      setSources(response)
      setLoadingSources(false)
    } catch (error) {
      setSources([])
      setLoadingSources(false)
    }
  }

  const getWorkUnit = async () => {
    try {
      setLoadingWorkUnitList(true)
      const allWorkUnit = await workUnitPaymentServices.getByAll()
      setWorkUnitList(allWorkUnit)
      setLoadingWorkUnitList(false)
    } catch (error) {
      setWorkUnitList([])
      setLoadingWorkUnitList(false)
    }
  }

  const handleImport = async (data) => {
    if (file?.file) {
      try {
        setLoading(true)
        setFileError(false)
        const formData = new FormData()
        formData.append('file', file.file)
        const importStatus = await installmentServices.import(data, formData)
        if (importStatus === 200) {
          notify('ra.message.import_success', { type: 'success' })
        } else {
          notify('ra.message.import_error', { type: 'error' })
        }
      } catch (error) {
        notify('ra.message.import_error', { type: 'error' })
      } finally {
        setLoading(false)
        onCloseModal()
      }
    } else {
      setFileError(true)
    }
  }

  const onOpenModal = () => {
    setOpenModal(true)
  }

  const onCloseModal = () => {
    setOpenModal(false)
    setFile(null)
    setFileError(false)
  }

  const handleSelectFile = (selectedFile) => {
    if (selectedFile.length > 0) {
      const file = selectedFile[0];
      setFile({
        name: file.name,
        file
      })
    }
  }

  const handleDownloadFile = () => {
    if (file?.file) {
      const url = URL.createObjectURL(file.file)
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }

  const handleDeleteFile = (event) => {
    event.stopPropagation()
    setFile(null)
  }

  return (
    <>
      <Button
        onClick={onOpenModal}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : <Upload fontSize='small' />}
        size='small'
        sx={{ margin: null, lineHeight: '1.5'}}
      >
        {translate('ra.action.import')}
      </Button>

      <Dialog open={openModal} onClose={onCloseModal} maxWidth='md' fullWidth className='import-installment-modal'>
        <DialogTitle display='flex' justifyContent='space-between'>
          {translate('ra.title.import_installment')}
          <IconButton size='small' onClick={onCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Form onSubmit={handleImport}>
          <DialogContent>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.work_unit')} className='small' required>
                {loadingWorkUnitList ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='work_unit_id'
                    options={workUnitList}
                    validate={[required()]}
                    optionsText={['long_name']}
                    size='small'
                    showSearch
                    limitOptions={false}
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.product_type')} className='small' required>
                {loadingProductTypeList ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <SelectInput
                    source='product_id'
                    options={productTypeList}
                    validate={[required()]}
                    optionsText={['long_name']}
                    size='small'
                    showSearch
                    showAllOption
                    hideInclude
                    multiple
                    limitOptions={false}
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.source')} className='small' required>
                {loadingSources ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='sources'
                    options={sources}
                    validate={[required()]}
                    optionsText={['long_name']}
                    showSearch
                    showAllOption
                    hideInclude
                    multiple
                    limitOptions={false}
                  />
                )}
              </EditInputsWrap>
              <Grid item xs={12}>
                <Button
                  variant='standard'
                  startIcon={<Add />}
                  style={{ color: blue }}
                  component='label'
                >
                  {translate('ra.action.add_file')}
                  <input
                    type='file'
                    hidden
                    // disabled={loadingFile}
                    inputProps={{ multiple: false }}
                    accept=".csv,.xlsx,.xlsm,.xltx"
                    onClick={(e) => e.target.value = null}
                    onChange={(e) => handleSelectFile(e.target.files)}
                  />
                </Button>
              </Grid>
              {file && (
                <Grid item xs={12}>
                  <div className='file' onClick={handleDownloadFile}>
                    <InsertDriveFile color='primary' className='file-icon' />
                    <Tooltip title={file?.name}>
                      <Typography className='name'>{file?.name}</Typography>
                    </Tooltip>
                    <IconButton
                      className='delete-icon'
                      size='small'
                      onClick={handleDeleteFile}
                    >
                      <Delete color='error' />
                    </IconButton>
                  </div>
                </Grid>
              )}
              {fileError && (
                <FormHelperText error style={{ marginLeft: 30 }}>
                  {translate('ra.validation.required')}
                </FormHelperText>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCloseModal} variant='outlined'>
              {translate('ra.action.cancel')}
            </Button>
            <LoadingButton
              loading={loading}
              loadingPosition='start'
              startIcon={<Upload />}
              variant='contained'
              type='submit'
            >
              {translate('ra.action.import')}
            </LoadingButton>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  )
}
